import { useState, useEffect, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import NotificationContext from "../context/NotificationContext";
import Navbar from "../components/Navbar";
import NotesDisplay from "../components/NotesDisplay";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useViewPermission } from "../context/PermissionContext";
import {
  dropdownListBody,
  normalizeListResponse,
} from "../utils/listPagination";
import * as gradesApi from "../api/grades";
import * as subjectsApi from "../api/subjects";
import * as topicsApi from "../api/topics";
import * as worksheetsApi from "../api/worksheets";

export default function ClassWorksheets() {
  const { isAuthenticated } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const { hasPermission, loading: permLoading } = useViewPermission("view_class_worksheets");

  // Dropdown states
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState("");
  const [worksheetItems, setWorksheetItems] = useState(null);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  useEffect(() => {
    if (!hasPermission) return;
    const fetchGrades = async () => {
      try {
        setLoading(true);
        const response = await gradesApi.list(dropdownListBody());
        setGrades(normalizeListResponse(response.data).data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching grades:", err);
        setLoading(false);
      }
    };
    const fetchSubjects = async () => {
      try {
        setLoading(true);
        const response = await subjectsApi.list(dropdownListBody());
        setSubjects(normalizeListResponse(response.data).data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching subjects:", err);
        setLoading(false);
      }
    };

    fetchGrades();
    fetchSubjects();
  }, [hasPermission]);
  const [topics, setTopics] = useState([]);
  useEffect(() => {
    if (!hasPermission) return;
    const fetchTopics = async () => {
      try {
        if (!subject || !grade) {
          setTopics([]);
          setLoading(false);
          return;
        }
        const res = await topicsApi.bySubjectAndGrade(
          subject,
          grade,
          dropdownListBody(),
        );
        setTopics(normalizeListResponse(res.data).data);
        setTopic("");
        setLoading(false);
      } catch (err) {
        console.error("Error fetching grades:", err);
        setLoading(false);
      }
    };

    fetchTopics();
  }, [subject, grade, hasPermission]);

  // Search Button Handler
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!hasPermission) return;
    try {
      const response = await worksheetsApi.byTopic(topic);
      setWorksheetItems(response?.data?.items || []);
      if (!response?.data?.items?.length) {
        notificationCtx.show({
          message: "No worksheet found for selected options.",
          stat: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching worksheet:", error);
      setWorksheetItems([]);
      const status = error?.response?.status;
      notificationCtx.show({
        message:
          status === 404
            ? "No worksheet found for selected options."
            : "Failed to fetch worksheet.",
        stat: "error",
      });
    }
  };

  if (loading || permLoading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      {/* {isAuthenticated && ( */}
      <div className="row justify-content-center align-items-start px-4 pt-5 mt-5">
        {/* LEFT PANEL */}
        <div
          className="col-md-3"
          style={{
            backgroundColor: "#f9e1be",
            borderRadius: "12px",
            padding: "72px 24px 0",
            position: "sticky", // <-- make it stick
            top: "15vh", // <-- stick to top of viewport
            alignSelf: "flex-start", // ensures proper positioning in flex row
            height: "70vh",
            overflowY: "auto", // allow internal scroll if needed
          }}
        >
          <div className="text-center">
            <h3 className="fw-bold mb-5">WORKSHEETS</h3>
            <form
              className="row g-2 justify-content-center align-items-end"
              onSubmit={handleSearch}
            >
              <div className="col-auto">
                <select
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  className="form-control mb-3"
                  style={{ minWidth: "220px" }}
                  required
                >
                  <option value="" disabled>
                    Select Grade
                  </option>
                  {grades.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-auto">
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="form-control mb-3"
                  style={{ minWidth: "220px" }}
                  required
                >
                  <option value="" disabled>
                    Select Subject
                  </option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-auto">
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="form-control mb-3"
                  style={{ minWidth: "220px" }}
                  required
                >
                  <option value="" disabled>
                    Select Topic
                  </option>
                  {topics.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.topicName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-auto">
                <button
                  type="submit"
                  className="btn btn-primary mt-3"
                  style={{
                    backgroundColor: "#f2c4c4",
                    borderColor: "#f2c4c4",
                    borderRadius: "15px",
                    minWidth: "220px",
                  }}
                  disabled={!grade || !subject || !topic || topic === ""}
                >
                  SEARCH
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div
          className="col-md-9 px-5"
          style={{
            minHeight: "70vh",
          }}
        >
          {!worksheetItems ? (
            <div className="d-flex flex-column align-items-center pt-5">
              <h4 className="mb-3 text-center text-secondary">
                Please select options from the left panel and click Search to
                load any worksheets if available.
              </h4>
            </div>
          ) : (
            <>
              <h4 className="mb-4 fw-bold">Worksheet</h4>
              <NotesDisplay
                items={worksheetItems}
                urlField="worksheetUrl"
                descriptionField="sheetDescription"
                progressKeyPrefix="worksheetItem"
              />
              <h4 className="my-4 fw-bold">Solution</h4>
              <NotesDisplay
                items={worksheetItems.filter((item) => item.solutionUrl)}
                urlField="solutionUrl"
                descriptionField="solutionDescription"
                progressKeyPrefix="worksheetItem"
                emptyMessage="No solutions available for the selected options."
              />
            </>
          )}
        </div>
      </div>
      {/* )} */}
    </div>
  );
}
